import React, { useRef, useState } from 'react'
import Menu from 'components/menu'
import Input from 'components/input'
import Tooltip from 'components/tooltip'
import Button from 'components/button'
import { ChevronDown } from 'components/icons'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { COLUMN_TYPE_NAMES, DEFAULT_COLUMN_TYPE, SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'
import { ColumnTypePicker } from './useColumnTypePicker'
import { ColumnTypePickerProvider, usePicker } from './context'
import ColumnConfig from './config'

interface ColumnTypePickerMenuProps {
  id: string
  picker: ColumnTypePicker
  width?: number
  zIndex?: number
}

// Width of the type-picker dropdown. The shared Menu clamps this to the viewport.
const MENU_WIDTH = 340

// Minimum gap kept between the picker and any screen edge (matches the tables' edge spacing), so the
// menu — opened from the far-right + button — is never flush against the edge and hard to use.
const VIEWPORT_MARGIN = 44

const CONFIGURE_FIRST_TYPES: IColumnTypes[] = SELECT_COLUMN_TYPES

// Stage 1: search + keyboard-navigable list of column types. Clicking a row (or Enter/Space on a
// focused one) adds the column instantly (quick add) — except for the configure-first types, which open
// the configure stage; the trailing chevron (or the → key) opens the configure stage for any type.
// Arrow keys move focus between options as a listbox.
const TypeList: React.FC = () => {
  const { typeSearch, setTypeSearch, filteredTypes, searchInputRef, quickAdd, configure } = usePicker()
  const listRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState<number>(-1)

  // Picking a type (click, Enter/Space, or Enter in the search box) quick-adds it unless it needs
  // configuring first.
  const pick = (type: IColumnTypes): void => {
    if (CONFIGURE_FIRST_TYPES.includes(type)) configure(type)
    else quickAdd(type)
  }

  // Move focus to the option at `index`, wrapping past either end. Focusing scrolls it into view.
  const focusOptionAt = (index: number): void => {
    const options = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]')
    if (!options || options.length === 0) return
    const wrapped = ((index % options.length) + options.length) % options.length
    options[wrapped]?.focus()
  }

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, type: IColumnTypes, index: number): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusOptionAt(index + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        focusOptionAt(index - 1)
        break
      case 'Home':
        event.preventDefault()
        focusOptionAt(0)
        break
      case 'End':
        event.preventDefault()
        focusOptionAt(-1)
        break
      case 'Enter':
      case ' ':
        // Prevent Space from scrolling the list; both keys pick the focused type.
        event.preventDefault()
        pick(type)
        break
      case 'ArrowRight':
        // Drill into the configure stage for the focused type.
        event.preventDefault()
        configure(type)
        break
    }
  }

  return (
    <div className="p-2.5">
      <Input
        id="add-column-type-search"
        ref={searchInputRef}
        type="text"
        value={typeSearch}
        placeholder="Search types…"
        className="mb-2"
        onChange={(event) => setTypeSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && filteredTypes.length > 0) {
            // Step down out of the search box into the list.
            event.preventDefault()
            focusOptionAt(0)
            return
          }
          if (event.key !== 'Enter') return
          // An empty search leaves the whole list "filtered", so its first entry is simply whatever
          // sorts first alphabetically (Attachment) — never what the user meant. Enter on the untouched
          // picker adds the default type; only an active search applies its top match (#10559).
          if (typeSearch.trim().length === 0) pick(DEFAULT_COLUMN_TYPE)
          else if (filteredTypes.length > 0) pick(filteredTypes[0])
        }}
      />
      <div ref={listRef} className="max-h-[320px] overflow-y-auto" role="listbox" aria-label="Column type">
        {filteredTypes.length === 0 ? (
          <div className="p-2.5 text-muted">No matching types</div>
        ) : (
          filteredTypes.map((type, index) => (
            <div
              key={type}
              data-testid={`add-column-type-${type}`}
              role="option"
              aria-selected={activeIndex === index}
              tabIndex={0}
              className="flex items-center gap-2.5 rounded px-2.5 py-1.5 cursor-pointer hover:bg-surface-sunken focus:bg-light-blue focus:outline-2 focus:outline-blue focus:outline-offset-[-2px]"
              onClick={() => pick(type)}
              onFocus={() => setActiveIndex(index)}
              onKeyDown={(event) => handleOptionKeyDown(event, type, index)}
            >
              <IconSelector kind={type} />
              <span className="truncate flex-1">{COLUMN_TYPE_NAMES[type]}</span>
              <Tooltip content="Configure option">
                <Button
                  type="button"
                  internalType="transparent"
                  aria-label="Configure option"
                  data-testid={`add-column-configure-${type}`}
                  // A fixed 24px square: the Button's default 32px height set
                  // the row's height, making every type 52px tall.
                  className="h-6 w-6 p-0 text-faint hover:text-body rounded"
                  onClick={(event) => {
                    event.stopPropagation()
                    configure(type)
                  }}
                >
                  <span className="flex -rotate-90">
                    <ChevronDown />
                  </span>
                </Button>
              </Tooltip>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// The add-column menu, used by the header + button and by the column context menu's Insert (Before)
// action. Renders the type list (stage 1) or the configure form (stage 2) inside a shared Menu.
const ColumnTypePickerMenu: React.FC<ColumnTypePickerMenuProps> = ({ id, picker, width = MENU_WIDTH, zIndex }) => {
  const { menuState, setMenuState, stage, handleClose } = picker

  return (
    <Menu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={zIndex}
      onClose={handleClose}
      viewportMargin={VIEWPORT_MARGIN}
    >
      <ColumnTypePickerProvider picker={picker}>
        {/* Stop clicks inside the picker from bubbling (through the portal's React tree) to the +
            button / menu item that owns this picker, which would re-open or double-trigger it. */}
        <div onClick={(event) => event.stopPropagation()}>{stage === 'type' ? <TypeList /> : <ColumnConfig />}</div>
      </ColumnTypePickerProvider>
    </Menu>
  )
}

export default ColumnTypePickerMenu
